import { apiGet } from '../../lib/api'
import { withAuth } from '../auth/token'
import type { SecurementGear, TruckAvailability } from '../availability/api'
import type { EquipmentFeature } from '../carriers/api'
import type { Load } from '../loads/api'

/** The figures a pair is ranked on, as the server's verdict found them. */
export interface MatchFactors {
  /** Straight-line miles from the unit to the first pickup; null when either place has no point. */
  deadheadMiles: number | null
  tripMiles: number | null
  /** The rate over the trip. */
  ratePerMile: number | null
  /** The rate over the deadhead and the trip together. */
  allInRatePerMile: number | null
  minRatePerMile: number | null
  /** A flag, never an exclusion; null when either side is missing. */
  belowMinimum: boolean | null
  toPreferredState: boolean
  daysAfterAvailable: number
}

/**
 * Why a pair cannot go, and what it still asks to confirm — codes, translated at render.
 * Kept as strings: a code this build does not know gets a translated generic sentence,
 * never the server's words.
 */
export interface MatchReasons {
  exclusions: string[]
  cautions: string[]
  /** The letters of DAT's code Hugo's table does not explain, e.g. "M" (TRKC-100). */
  requirementLetters: string | null
  /** What the load asks for and the unit does not state — air ride, a lift gate (TRKC-100). */
  missingFeatures: EquipmentFeature[]
  /** The tie-down gear the load asks for and the posting does not declare — DAT's FC chains. */
  missingSecurement: SecurementGear[]
}

export interface LoadMatch extends MatchReasons {
  load: Load
  factors: MatchFactors
  brokerVerified: boolean
  brokerPhone: string | null
  brokerEmail: string | null
}

export interface PostingMatch extends MatchReasons {
  posting: TruckAvailability
  factors: MatchFactors
}

/** The loads a posted unit could take, best first, and the ones it could not. */
export interface PostingMatches {
  posting: TruckAvailability
  compatible: LoadMatch[]
  notCompatible: LoadMatch[]
}

/** The posted units that could take a load, closest first, and the ones that could not. */
export interface LoadMatches {
  load: Load
  brokerVerified: boolean
  /** The brokerage's own phone and email, for a load that names no agent. */
  brokerPhone: string | null
  brokerEmail: string | null
  compatible: PostingMatch[]
  notCompatible: PostingMatch[]
}

export function getPostingMatches(postingId: string): Promise<PostingMatches> {
  return apiGet<PostingMatches>(`/api/availability/${postingId}/matches`, { headers: withAuth() })
}

export function getLoadMatches(loadId: string): Promise<LoadMatches> {
  return apiGet<LoadMatches>(`/api/loads/${loadId}/matches`, { headers: withAuth() })
}
